import { Banner, Button, InlineStack, Select, Spinner, Text } from "@shopify/polaris";
import { useEffect, useMemo, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import type { FitmentCatalogSettings } from "../../app/models/FitmentCatalog";
import type { StorefrontResultsSettings, StorefrontUiSettings } from "../../app/models/StorefrontResultsSettings";
import type { StorefrontThemeSettings } from "../../app/models/StorefrontThemeSettings";
import { getVisibleFitmentFields, normalizeFitmentKey } from "../../app/utils/fitmentFields";
import PreviewProductImage from "./PreviewProductImage";
import { sampleGroups, sampleVehicles, storefrontUrl, type PreviewScenario } from "./searchPreviewData";
import "./AppearancePreview.css";

export type AppearancePreviewAssets = { shell: string; renderer: string; bridge: string };

// Only application-owned source goes into srcDoc. Merchant text/data are sent
// separately by postMessage and rendered as text by the storefront renderer.
export function appearancePreviewDocument(assets: AppearancePreviewAssets, channelId: string, vehicleBarInTheme = false) {
    const script = (source: string) => source.replace(/<\/script/gi, "<\\/script");
    return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src https: data:; connect-src 'none'; form-action 'none'; base-uri 'none'">
<style>html{font:16px Arial,sans-serif;line-height:1.5}body{margin:0;overflow-x:hidden}*,*::before,*::after{box-sizing:border-box}button,input,select{font:inherit}${vehicleBarInTheme ? '#gv-mmy-bar-proxy{display:none!important}' : ''}</style>
</head><body>${assets.shell}<script>window.GV_PARTFINDER_APPEARANCE_PREVIEW=true;window.GV_PARTFINDER_APPEARANCE_CHANNEL=${JSON.stringify(channelId)};</script>
<script>${script(assets.renderer)}</script><script>${script(assets.bridge)}</script></body></html>`;
}

export function appearancePreviewData(results: StorefrontResultsSettings, ui: StorefrontUiSettings,
    catalog: FitmentCatalogSettings, theme: StorefrontThemeSettings, text: Record<string, string>, locale: string | undefined, scenario: PreviewScenario) {
    const fields = getVisibleFitmentFields(catalog);
    const images = new Map<string, string>();
    const groups = sampleGroups(results, scenario).map(group => ({
        key: group.id === "all" ? "products" : group.id, title: group.id === "all" ? "Products" : group.title,
        products: group.products.flatMap(product => {
            if (!images.has(product.image)) images.set(product.image,
                `data:image/svg+xml;charset=utf-8,${encodeURIComponent(renderToStaticMarkup(<PreviewProductImage kind={product.image} />))}`);
            const variants = results.variantResultDisplayMode === "SeparateVariants" ? product.variants : [""];
            return variants.map((variant, index) => ({
                id: `${product.id}-${index}`, title: product.title + (variant && product.variants.length > 1 ? ` · ${variant}` : ""),
                price: Number(product.price.replace(/[^\d.]/g, "")), currencyCode: "USD", hidePrice: results.priceDisplayMode === "HidePrices",
                featuredImage: images.get(product.image), fitment: product.id === "oil" ? "Universal" : "ExactFit",
                productUrl: "#sample-product", cartVariantId: index + 1, vendor: "PartFits sample", sku: `SAMPLE-${product.id.toUpperCase()}`,
                isInStock: true, fitmentNotes: [product.variants[0]], reviewRating: 4.5, reviewCount: 18,
            }));
        }),
    }));
    const prices = groups.flatMap(group => group.products.map(product => product.price));
    return {
        ui, text, locale, automaticSearch: !!theme.automaticSearch,
        fields: fields.map(field => ({
            key: normalizeFitmentKey(field), required: field.isFilterable,
            label: text[`selector_field.${field.id || `field-${field.fieldIndex}`}.display_label`] || field.label,
            placeholder: text[`selector_field.${field.id || `field-${field.fieldIndex}`}.placeholder`] || text[`search.prompt_${normalizeFitmentKey(field)}`] || field.placeholder || `Select ${field.label}`,
            numeric: field.sortMode !== "Alphabetical" || field.dataType === "Number" || field.dataType === "Decimal", desc: field.sortDirection === "Desc",
        })),
        rows: sampleVehicles(fields, catalog.name),
        response: {
            renderingMode: "AppProxyResults", groupResultsBy: results.groupResultsBy, loadingMode: "all", groups,
            filters: { enabled: results.groupResultsBy === "None" && results.priceDisplayMode !== "HidePrices", groups: [],
                price: prices.length ? { min: Math.min(...prices), max: Math.max(...prices), currencyCode: "USD", selectedMin: "", selectedMax: "" } : {} },
        },
    };
}

type Props = {
    results: StorefrontResultsSettings; theme: StorefrontThemeSettings; uiSettings: StorefrontUiSettings;
    catalog?: FitmentCatalogSettings | null; catalogError?: string; onRetryCatalog?: () => void;
    text?: Record<string, string>; locale?: string; textError?: string; onRetryText?: () => void; shopDomain?: string; vehicleBarInTheme?: boolean;
};

export default function AppearancePreview({ results, theme, uiSettings, catalog, catalogError, onRetryCatalog,
    text = {}, locale, textError, onRetryText, shopDomain, vehicleBarInTheme = false }: Props) {
    const [assets, setAssets] = useState<AppearancePreviewAssets>();
    const [error, setError] = useState(false);
    const [retry, setRetry] = useState(0);
    const [ready, setReady] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [scenario, setScenario] = useState<PreviewScenario>("matches");
    const frame = useRef<HTMLIFrameElement>(null);
    const dialog = useRef<HTMLDialogElement>(null);
    const storefront = storefrontUrl(shopDomain);
    const data = useMemo(() => catalog ? appearancePreviewData(results, uiSettings, catalog, theme, text, locale, scenario) : null,
        [results, uiSettings, catalog, theme, text, locale, scenario]);
    const latestData = useRef(data);
    latestData.current = data;
    // App Bridge portals modal content into another document. The preview's
    // immediate parent is then the modal window, not this React app's window.
    // A per-component nonce lets the frame bind its reply target on load without
    // trusting arbitrary windows or granting the sandbox same-origin access.
    const [channelId] = useState(() => Array.from(crypto.getRandomValues(new Uint32Array(4)), value => value.toString(16).padStart(8, "0")).join(""));
    const document = useMemo(() => assets ? appearancePreviewDocument(assets, channelId, vehicleBarInTheme) : undefined, [assets, channelId, vehicleBarInTheme]);

    useEffect(() => {
        const controller = new AbortController();
        setError(false); setAssets(undefined); setReady(false);
        const api = (process.env.REACT_APP_API_URL || "/api").replace(/\/$/, "");
        // Version the assets so cached code cannot omit the sandboxed view-toggle fix.
        fetch(`${api}/partfinder/appearance-preview-assets?v=4`, { signal: controller.signal, credentials: "omit" })
            .then(response => { if (!response.ok) throw new Error("Preview assets unavailable"); return response.json(); })
            .then(value => {
                if (![value.shell, value.renderer, value.bridge].every(item => typeof item === "string")) throw new Error("Invalid preview assets");
                if (!controller.signal.aborted) setAssets(value);
            }).catch(() => { if (!controller.signal.aborted) setError(true); });
        return () => controller.abort();
    }, [retry]);
    useEffect(() => {
        const receive = (event: MessageEvent) => {
            if (event.source !== frame.current?.contentWindow) return;
            if (event.data?.type === "partfits:appearance-ready") {
                setReady(true);
                if (latestData.current) frame.current?.contentWindow?.postMessage({ type: "partfits:appearance-update", settings: latestData.current }, "*");
            }
            if (event.data?.type === "partfits:appearance-error") setError(true);
        };
        window.addEventListener("message", receive);
        return () => window.removeEventListener("message", receive);
    }, []);
    useEffect(() => {
        if (ready && data) frame.current?.contentWindow?.postMessage({ type: "partfits:appearance-update", settings: data }, "*");
    }, [ready, data]);
    useEffect(() => {
        if (!document || !data?.fields.length || ready) return;
        const timeout = window.setTimeout(() => setError(true), 15000);
        return () => window.clearTimeout(timeout);
    }, [document, data, ready, expanded]);
    useEffect(() => {
        if (expanded) dialog.current?.showModal();
    }, [expanded]);
    const close = () => { dialog.current?.close(); setExpanded(false); setReady(false); };
    const preview = <>
        <div className="pf-appearance-preview__toolbar">
            <div><Text as="h2" variant="headingMd">Live preview</Text><Text as="p" tone="subdued">Storefront layout · sample products · unsaved settings</Text></div>
            <InlineStack gap="200">
                <Button onClick={() => { setReady(false); setRetry(value => value + 1); }}>Reset preview</Button>
                <Button onClick={() => { if (expanded) close(); else { setReady(false); setExpanded(true); } }}>{expanded ? "Close preview" : "Expand preview"}</Button>
            </InlineStack>
        </div>
        <div className="pf-appearance-preview__scenario"><Select label="Sample results" labelInline value={scenario} onChange={value => setScenario(value as PreviewScenario)} options={[
            { label: "Matching products", value: "matches" }, { label: "No matches", value: "empty" }, { label: "Matching variants", value: "variants" },
        ]} /></div>
        {catalogError ? <Banner tone="critical"><p>Could not load selector fields.</p><Button onClick={onRetryCatalog}>Retry fields</Button></Banner> : null}
        {textError ? <Banner tone="warning"><p>Storefront text could not be loaded. Showing default text.</p><Button onClick={onRetryText}>Retry text</Button></Banner> : null}
        {error ? <Banner tone="critical"><p>The storefront preview could not load.</p><Button onClick={() => setRetry(value => value + 1)}>Retry preview</Button></Banner>
            : !document || !data ? <div className="pf-appearance-preview__loading"><Spinner accessibilityLabel="Loading storefront preview" /></div>
                : !data.fields.length ? <p className="pf-appearance-preview__note">Enable selector fields to preview the vehicle search.</p>
                    : <iframe ref={frame} title="PartFits storefront appearance" sandbox="allow-scripts" srcDoc={document}
                        onLoad={() => frame.current?.contentWindow?.postMessage({ type: "partfits:appearance-init", channelId }, "*")}
                        className="pf-appearance-preview__frame" />}
        <div className="pf-appearance-preview__note">Your store’s header, fonts and theme overrides are not included. Preview actions never save settings or change a cart.
            {storefront && <> <a href={storefront} target="_blank" rel="noreferrer">Open storefront</a></>}
        </div>
    </>;
    return <section className="pf-appearance-preview" aria-label="Appearance preview">
        {expanded ? <dialog ref={dialog} className="pf-appearance-preview__dialog" aria-label="Expanded appearance preview" onCancel={event => { event.preventDefault(); close(); }}>{preview}</dialog> : preview}
    </section>;
}
